import React, { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useParams } from 'react-router-dom';

import { Section } from 'components/Section';
import { Spinner } from 'components';
import MobileNavigationTabs from 'components/Menu/MobileNavigationTabs';
import { useEventsApi, useLedger } from 'data';
import { buildCurrencyBalance, getCurrencySymbolForCurrencyCode } from 'helpers/currency';
import { isObjectEmpty } from 'helpers';
import useMediaQuery from 'hooks/mediaQuery/useMediaQuery';
import useEventUri from 'hooks/useEventUri';
import FreezeWristband from 'pages/EventBalance/components/FreezeWristband';
import WalletInfo from 'pages/EventBalance/components/WalletInfo';
import { selectEventCurrency, selectUserData, useAppSelector } from 'store';

const EventLayout: FC = () => {
  const isMobile = useMediaQuery();
  const { eventId } = useParams();
  const { fetchSelectedEventById } = useEventsApi();

  const selectedEvent = useSelector((state: any) => state.eventsData.selectedEvent);
  const eventUri = useEventUri();
  const currency = useSelector(selectEventCurrency);
  const { sub: accountId } = useAppSelector(selectUserData);

  const {
    fetchEventWallet,
    freezeWalletAction,
    unfreezeWalletAction,
    currentWalletBalance,
    currentWalletRFID,
    currentWallet,
    currentWalletId,
    isFetchingWallet,
    isLoading,
  } = useLedger();

  const [openFreezeWristbandModal, setOpenFreezeWristbandModal] = React.useState(false);
  const isWalletLoading = isFetchingWallet || isLoading;

  const currencySymbol = getCurrencySymbolForCurrencyCode(currency);
  const balance =
    currentWalletBalance >= 0
      ? buildCurrencyBalance(currency, currencySymbol, currentWalletBalance)
      : '';

  const title = selectedEvent?.eventSummary?.name?.en;
  const isWalletFrozen = Boolean(currentWallet?.wallet?.isFrozen);
  const areWalletPaymentsEnabled = Boolean(selectedEvent?.eventDetails?.enableWalletPayments);

  const handleFreezeAction = async () => {
    try {
      setOpenFreezeWristbandModal(false);
      const action = isWalletFrozen ? unfreezeWalletAction : freezeWalletAction;
      await action(selectedEvent?.uuid, currentWalletId, accountId);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (isObjectEmpty(selectedEvent)) {
      eventId && fetchSelectedEventById(eventId);
    }
  }, [selectedEvent, eventId, fetchSelectedEventById]);

  useEffect(() => {
    if (selectedEvent && eventUri) {
      fetchEventWallet(eventUri);
    }
  }, [selectedEvent, eventUri, fetchEventWallet]);

  const renderLoadingOrValue = (value: string | undefined, fallback = 'N/A') =>
    isWalletLoading ? <Spinner size={6} /> : value || fallback;

  const renderMobileView = () => (
    <>
      <div className='w-full px-3 pt-3'>
        <div className='flex justify-between items-center text-lg font-medium text-custom-primary'>
          <div>{title}</div>
          <div>{balance}</div>
        </div>
        {isWalletLoading ? (
          <Spinner size={6} />
        ) : (
          <WalletInfo
            currentWalletRFID={currentWalletRFID}
            currentWallet={currentWallet}
            setOpenFreezeWristbandModal={setOpenFreezeWristbandModal}
            isWalletFrozen={isWalletFrozen}
          />
        )}
      </div>
      <MobileNavigationTabs />
      <div className='px-4'>
        <Outlet />
      </div>
    </>
  );

  const renderDesktopView = () => (
    <>
      <div className='text-custom-primary text-xl font-bold pb-4'>{title}</div>
      <div className='flex gap-4 justify-between'>
        {areWalletPaymentsEnabled && (
          <Section
            label='Wallet Balance'
            value={renderLoadingOrValue(balance, '')}
            actionLabel={isWalletFrozen ? 'Unfreeze Wallet' : 'Freeze Wallet'}
            onActionClick={() => setOpenFreezeWristbandModal(true)}
          />
        )}
        <Section label='Wristband/Card' value={renderLoadingOrValue(currentWalletRFID)} />
        <Section label='Security ID' value={renderLoadingOrValue(currentWallet?.securityCode)} />
      </div>
      <div className='pt-4'>
        <Outlet />
      </div>
    </>
  );

  return (
    <>
      {isMobile ? renderMobileView() : renderDesktopView()}
      <FreezeWristband
        isOpen={openFreezeWristbandModal}
        onActionConfirmed={handleFreezeAction}
        onClose={() => setOpenFreezeWristbandModal(false)}
        isFrozen={isWalletFrozen}
      />
    </>
  );
};
export default EventLayout;
